import { useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import type { StrategyDetail, StrategyListItem, StrategyStatus } from "@/types/strategy.type";

const listKey = (brandId: string | null) => ["socialStrategies", brandId];
const detailKey = (strategyId: string | null) => ["socialStrategy", strategyId];

/** What going live answers with; `calendarScope` says what happened to the calendar's volume. */
export interface ActivateStrategyResult {
  calendarScope?: { status: "written" | "kept" | "no_volume" };
  /** The go-live checks still open; it went live anyway. */
  warnings?: string[];
}

/**
 * The strategy lifecycle transitions: activate (go live — promotes the pillars
 * and moves the brand's active pointer), archive and revert to draft. Every
 * edge runs both ways, so each of the three states is reachable from the
 * other two.
 *
 * Status changes are optimistic: the list and the open document show the new
 * status at once, and only the touched rows roll back on failure. Mirrors the
 * backend POST /social-strategies/:brandId/:strategyId/{action} routes.
 */
export function useStrategyLifecycle(
  brandId: string | null,
  strategyId: string | null,
) {
  const qc = useQueryClient();

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: listKey(brandId) });
    void qc.invalidateQueries({ queryKey: detailKey(strategyId) });
  };

  /** Writes `status` onto this strategy (and archives the old live one on going live). */
  const transition = (status: StrategyStatus) => ({
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: listKey(brandId) });
      const before = qc.getQueryData<StrategyListItem[]>(listKey(brandId));
      const now = new Date().toISOString();
      const touched = new Map<string, StrategyStatus>();
      qc.setQueryData<StrategyListItem[]>(listKey(brandId), (rows) =>
        rows?.map((row) => {
          if (row.id === strategyId) {
            touched.set(row.id, row.status);
            return { ...row, status, ...(status === "active" ? { liveAt: now } : {}) };
          }
          if (status === "active" && row.status === "active") {
            touched.set(row.id, row.status);
            return { ...row, status: "archived" as const };
          }
          return row;
        }),
      );
      qc.setQueryData<StrategyDetail>(detailKey(strategyId), (detail) =>
        detail ? { ...detail, strategy: { ...detail.strategy, status } } : detail,
      );
      return { touched, hadList: !!before };
    },
    onError: (_err: unknown, _vars: unknown, context?: { touched: Map<string, StrategyStatus> }) => {
      if (!context) return;
      qc.setQueryData<StrategyListItem[]>(listKey(brandId), (rows) =>
        rows?.map((row) => (context.touched.has(row.id) ? { ...row, status: context.touched.get(row.id)! } : row)),
      );
      const previous = strategyId ? context.touched.get(strategyId) : undefined;
      if (previous) {
        qc.setQueryData<StrategyDetail>(detailKey(strategyId), (detail) =>
          detail ? { ...detail, strategy: { ...detail.strategy, status: previous } } : detail,
        );
      }
    },
    onSettled: invalidate,
  });

  const postAction = <T,>(action: string) => async () => {
    const res = await axiosInstance.post<T>(
      `/social-strategies/${brandId}/${strategyId}/${action}`,
    );
    return res.data;
  };

  const activate = useMutation({ mutationFn: postAction<ActivateStrategyResult>("activate"), ...transition("active") });
  const archive = useMutation({ mutationFn: postAction<unknown>("archive"), ...transition("archived") });
  const revertToDraft = useMutation({ mutationFn: postAction<unknown>("draft"), ...transition("draft") });

  return { activate, archive, revertToDraft };
}

/** Pulls the backend error message ({ message } from ErrorHandler) off an axios error. */
export function lifecycleErrorMessage(err: unknown): string {
  const message = (err as { response?: { data?: { message?: string } } })?.response
    ?.data?.message;
  return typeof message === "string" && message.trim()
    ? message
    : "Something went wrong. Please try again.";
}
